import PropTypes from 'prop-types';
import React from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {TargetEntry} from 'folder-items/FileConstants';
import {EntrySummary} from 'notebook/ELNConstants';
import {Filter, FilterKey, keyToEmptyFilter, SearchContext, SortKey} from 'search/SearchConstants';
import ItemSearchInput, {Item} from 'ui-library/attach-dropdown/ItemSearchInput';
import {ChipSize} from 'ui-library/chips/Chip';
import {searchPost} from 'ui-library/inputs/ChipSearchInput';
import {getIconIdForSearchResult} from 'ui-library/inputs/ItemOptionContent';
import {OptionData} from 'ui-library/menus/MenuConstants';
import APIUtil from 'util/APIUtil';
import {ElementKeyboardEvent} from 'util/Types';

export interface EntrySearchInputProps {
  areChipsClickable?: boolean;
  autoFocus?: boolean;
  chipSize?: ChipSize;
  className?: string;
  disabled?: boolean;
  isReadOnly?: boolean;
  initialQuery?: string;
  onBlur?: () => void;
  onChange: (entrySummaries: Array<EntrySummary | TargetEntry | Item>) => void;
  onKeyDown?: (element: ElementKeyboardEvent) => void;
  selectedEntries: Array<EntrySummary | TargetEntry | Item>;
  shouldAllowMultiple?: boolean;
  query?: string;
  onUpdateQuery?: (query: string) => void;
  placeholder?: string;
  context?: SearchContext;
  additionalFilters?: Array<Filter>;
  hideSelectedResults?: boolean;
}

/**
 * Input to search and select one or more entries.
 *
 * This component takes selected values of shape Item, but calls the onChange handler with values of
 * shape Entry. This is technically incorrect as the underlying ItemSearchInput doesn't update the
 * selected item values, so onChange may be called with values of shape Item.
 */
export default function EntrySearchInput(props: EntrySearchInputProps): JSX.Element {
  const filters = [
    {...keyToEmptyFilter(FilterKey.TYPES), value: [ModelTypeConstants.ENTRY]},
    ...(props.additionalFilters ?? []),
  ];

  return (
    <ItemSearchInput<EntrySummary | TargetEntry | Item>
      areChipsClickable={props.areChipsClickable}
      autoFocus={props.autoFocus}
      chipSize={props.chipSize}
      className={props.className}
      disabled={props.disabled}
      isReadOnly={props.isReadOnly}
      doSearch={searchPost}
      initialQuery={props.initialQuery}
      itemKey="entrySummariesById"
      onKeyDown={props.onKeyDown}
      onChange={props.onChange}
      fetchParams={{
        filters,
        offset: 0,
        sorts: [{key: SortKey.SORT_RELEVANCE_KEY, reverse: false}],
        context: props.context,
      }}
      searchURL="/1/api/search"
      selectedItems={props.selectedEntries}
      shouldAllowMultiple={props.shouldAllowMultiple}
      query={props.query}
      onUpdateQuery={props.onUpdateQuery}
      onBlur={props.onBlur}
      placeholder={props.placeholder}
      hideSelectedResults={props.hideSelectedResults}
      getOptionDatasFromResponse={(resp) => {
        const idsToIgnore = props.selectedEntries.map((item) => item.id);
        const isWriteContext = props.context === SearchContext.FOR_WRITE_ACTION;
        const permissionsByModelId = (resp && resp.permissionsByModelId) || {};
        const filteredEntries = Object.entries(resp.entrySummariesById).filter(([id]) => {
          if (idsToIgnore.includes(id)) {
            return false;
          }
          if (isWriteContext) {
            return permissionsByModelId[id]?.writable === true;
          }
          return true;
        });
        return filteredEntries.map(
          ([id, item]: [string, EntrySummary | TargetEntry | Item]): OptionData => ({
            name: item.name ?? id,
            icon: getIconIdForSearchResult({item, type: APIUtil.apiIdToType(id)}),
            value: {
              item,
              item_id: id,
              name: item.name ?? id,
              type: APIUtil.apiIdToType(id),
            },
          })
        );
      }}
    />
  );
}

EntrySearchInput.propTypes = {
  areChipsClickable: PropTypes.bool,
  autoFocus: PropTypes.bool,
  chipSize: PropTypes.oneOf(Object.values(ChipSize)),
  className: PropTypes.string,
  disabled: PropTypes.bool,
  initialQuery: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onKeyDown: PropTypes.func,
  selectedEntries: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string, // may be null if entry has been deleted
    })
  ).isRequired,
  shouldAllowMultiple: PropTypes.bool,
  query: PropTypes.string,
  onUpdateQuery: PropTypes.func,
};

type EntrySearchInputWithItemsProps = {
  selectedItems: Array<Item>;
} & Omit<EntrySearchInputProps, 'selectedEntries'>;

/**
 * Input to search and select one or more entries.
 *
 * Use this component when only the ids of the entries are available and needed. It expects the selected
 * values to be of shape Item, and calls the onChange handler with values of shape Item.
 */
export function EntrySearchInputWithItems(props: EntrySearchInputWithItemsProps): JSX.Element {
  return <EntrySearchInput {...props} selectedEntries={props.selectedItems} />;
}
